// ────────────────────────────────────────────────────────────────────────────
// Cairn glossary — Slice 8.
//
// Friendly, plain-language explanations of the tax / finance / Cairn-specific
// jargon that shows up across the app. The goal is *one click to understand*
// any term the user doesn't recognize. Tone is "Khan Academy explainer" — the
// reader is smart but new to the term.
//
// Editorial rules:
//   - `short` ≤ 180 chars; readable as a hover preview.
//   - `long` is 1–3 short paragraphs separated by `\n\n`. Concrete numbers
//     where helpful. No academic jargon. No patronizing tone.
//   - Every term has at least one external link. Prefer .gov for tax / IRS
//     terms; Wikipedia / Investopedia are fine for general concepts. We do
//     NOT fabricate specific URL slugs we're not confident exist; landing
//     pages beat broken deep links.
//   - Numbers cited (limits, brackets) are 2026 unless noted otherwise. Keep
//     them in sync with `src/tax/data.ts`.
//   - `currentValue` is optional — set when there's a single live number the
//     user wants at a glance (e.g. the 402(g) limit).
//
// This file is the single source of truth for every glossary entry; the panel
// UI, the global `Glossary` page, and the `<GlossaryTerm>` inline component
// all read from `GLOSSARY` below.
// ────────────────────────────────────────────────────────────────────────────

export type GlossaryCategory =
  | 'irs_limits'
  | 'account_types'
  | 'tax_concepts'
  | 'medicare_aca'
  | 'social_security'
  | 'mortgage_loans'
  | 'retirement_planning'
  | 'cairn_terms';

export interface GlossaryLink {
  label: string;
  href: string;
}

export interface GlossaryTerm {
  /** Stable slug; never changes once shipped. */
  id: string;
  /** Display name (may contain punctuation and superscripts). */
  term: string;
  /** Other names users might search by. */
  aliases?: string[];
  /** Plain-language one-liner, ≤180 chars. Shown at the top of the panel and
   *  as a hover preview. */
  short: string;
  /** 1–3 short paragraphs, plain language. Use \n\n between paragraphs.
   *  Concrete numerical examples encouraged. */
  long: string;
  /** Optional current-year live value (e.g., "$23,500 for 2026"). */
  currentValue?: string;
  /** External authoritative resources for deeper learning. Prefer .gov first.
   *  At least one entry required. */
  links: GlossaryLink[];
  /** Other term ids the user might want to read next. */
  related?: string[];
  /** Loose category for grouping on the global glossary page. */
  category: GlossaryCategory;
}

export const CATEGORY_LABELS: Record<GlossaryCategory, string> = {
  irs_limits: 'IRS limits',
  account_types: 'Account types',
  tax_concepts: 'Tax concepts',
  medicare_aca: 'Medicare & ACA',
  social_security: 'Social Security',
  mortgage_loans: 'Mortgage & loans',
  retirement_planning: 'Retirement planning',
  cairn_terms: 'Cairn-specific terms',
};

export const CATEGORY_ORDER: GlossaryCategory[] = [
  'irs_limits',
  'account_types',
  'tax_concepts',
  'medicare_aca',
  'social_security',
  'mortgage_loans',
  'retirement_planning',
  'cairn_terms',
];

// ────────────────────────────────────────────────────────────────────────────
// The terms.
// ────────────────────────────────────────────────────────────────────────────

export const GLOSSARY: GlossaryTerm[] = [
  // ── IRS limits ────────────────────────────────────────────────────────────
  {
    id: 'irs_402g',
    term: '402(g) limit',
    aliases: ['402g', '401(k) elective deferral limit', 'employee deferral cap'],
    short:
      'The IRS cap on how much YOU (the employee) can defer into a 401(k), 403(b), or 457(b) plan in a year.',
    long:
      'IRC §402(g) caps the dollars you personally route from your paycheck into a 401(k)/403(b)/457(b). For 2026 that limit is $23,500. Pre-tax and Roth contributions share the same bucket — if you put $20,000 into pretax 401(k), you only have $3,500 of room left for Roth 401(k) that year.\n\nThe 402(g) cap does NOT count employer match, employer profit-sharing, or after-tax (non-Roth) employee contributions. Those are governed separately by 415(c).\n\nIf you turn 50 or older this year, the cap goes up by a "catch-up" amount — see the catch-up term. If you turn 60-63, the SECURE 2.0 super catch-up gives you even more room.',
    currentValue: '$23,500 for 2026 (under age 50)',
    links: [
      {
        label: 'IRS — Retirement topics: 401(k) and Profit-Sharing Plan Contribution Limits',
        href: 'https://www.irs.gov/retirement-plans/plan-participant-employee/retirement-topics-401k-and-profit-sharing-plan-contribution-limits',
      },
      {
        label: 'IRS — Cost-of-living adjustments',
        href: 'https://www.irs.gov/retirement-plans',
      },
    ],
    related: ['irs_415c', 'catchup_50', 'super_catchup_60_63', 'pretax_401k', 'roth_401k'],
    category: 'irs_limits',
  },
  {
    id: 'irs_415c',
    term: '415(c) limit',
    aliases: ['415c', 'total annual additions', 'mega backdoor cap'],
    short:
      'The IRS cap on total dollars (employee + employer + after-tax) flowing into your 401(k) in one year.',
    long:
      'IRC §415(c) is the master cap on every dollar that lands in your 401(k) for a given year — your own pre-tax/Roth contributions, your employer\'s match, employer profit-sharing, AND after-tax (non-Roth) employee contributions. For 2026 that ceiling is $70,000.\n\nThe gap between 415(c) and 402(g) is what powers the "mega backdoor Roth" strategy. Example: if your employer plan allows after-tax contributions and in-service Roth conversions, and you maxed your $23,500 employee deferral plus got $10,000 in match, you still have $36,500 of 415(c) headroom. You could pour that into the after-tax sleeve and convert it to Roth — every dollar grows tax-free for life.\n\nNot every employer plan supports after-tax contributions or in-service conversions. Check your Summary Plan Description; if either is missing, the mega backdoor isn\'t available to you.',
    currentValue: '$70,000 for 2026 (under age 50)',
    links: [
      {
        label: 'IRS — Retirement topics: 401(k) Limits',
        href: 'https://www.irs.gov/retirement-plans/plan-participant-employee/retirement-topics-401k-and-profit-sharing-plan-contribution-limits',
      },
    ],
    related: ['irs_402g', 'after_tax_401k', 'mega_backdoor', 'catchup_50'],
    category: 'irs_limits',
  },
  {
    id: 'ira_limit',
    term: 'IRA contribution limit',
    aliases: ['ira limit', 'roth ira limit', 'traditional ira limit'],
    short:
      'The IRS cap on how much you can put into IRAs (Traditional + Roth combined) in a year.',
    long:
      'For 2026 the IRA contribution cap is $7,000 — combined across Traditional and Roth IRAs. If you contribute $4,000 to a Roth IRA, you have $3,000 of room left for Traditional IRA that year.\n\nIf you\'re 50 or older you get a $1,000 catch-up, lifting the cap to $8,000.\n\nRoth IRA also has income limits — at high enough MAGI you can\'t contribute directly. The "backdoor Roth" workaround handles that case (see backdoor Roth).',
    currentValue: '$7,000 for 2026 ($8,000 with age-50 catch-up)',
    links: [
      {
        label: 'IRS — IRA Contribution Limits',
        href: 'https://www.irs.gov/retirement-plans/plan-participant-employee/retirement-topics-ira-contribution-limits',
      },
    ],
    related: ['roth_ira', 'traditional_ira', 'backdoor_roth', 'catchup_50', 'magi'],
    category: 'irs_limits',
  },
  {
    id: 'catchup_50',
    term: 'Age-50 catch-up',
    aliases: ['catchup', 'catch up contribution', 'age 50 catch-up'],
    short:
      'Extra contribution room the IRS gives savers age 50 and older — applies to both 401(k) and IRA.',
    long:
      'Once you turn 50, the IRS lets you put more into tax-advantaged retirement accounts each year:\n\n- 401(k)/403(b)/457(b): an extra $7,500 on top of the regular 402(g) limit (so $23,500 + $7,500 = $31,000 in 2026).\n- IRA: an extra $1,000 (so $7,000 + $1,000 = $8,000 in 2026).\n\nYou get the catch-up for the entire year you turn 50, even if your birthday is in December. SECURE 2.0 introduced a separate, bigger "super catch-up" for ages 60–63 — see that entry for details.',
    currentValue: '+$7,500 (401k) and +$1,000 (IRA) for 2026',
    links: [
      {
        label: 'IRS — Catch-up contributions',
        href: 'https://www.irs.gov/retirement-plans/catch-up-contributions',
      },
    ],
    related: ['irs_402g', 'ira_limit', 'super_catchup_60_63'],
    category: 'irs_limits',
  },
  {
    id: 'super_catchup_60_63',
    term: 'Super catch-up (ages 60–63)',
    aliases: ['secure 2.0 catch-up', '60-63 catch-up', 'super catch up'],
    short:
      'A bigger 401(k) catch-up for savers ages 60–63, introduced by the SECURE 2.0 Act.',
    long:
      'The SECURE 2.0 Act (2022) created a higher "super catch-up" for workers in their early 60s. In the years you are 60, 61, 62, or 63, your 401(k)/403(b)/457(b) catch-up bumps from the regular $7,500 up to $11,250 for 2026.\n\nThis is on top of the regular 402(g) limit, so a 60-year-old in 2026 could defer $23,500 + $11,250 = $34,750 into their 401(k).\n\nIn the year you turn 64 the regular age-50 catch-up takes over again (back to $7,500). The super catch-up does NOT apply to IRAs — those still get the standard $1,000 catch-up.',
    currentValue: '+$11,250 for 2026 (instead of $7,500)',
    links: [
      {
        label: 'IRS — SECURE 2.0 Act',
        href: 'https://www.irs.gov/retirement-plans/secure-act-of-2019-and-secure-20-act-of-2022',
      },
    ],
    related: ['catchup_50', 'irs_402g'],
    category: 'irs_limits',
  },
  {
    id: 'rmd',
    term: 'Required Minimum Distribution (RMD)',
    aliases: ['required minimum distribution', 'rmds'],
    short:
      'A mandatory annual withdrawal the IRS forces from pretax retirement accounts once you reach a certain age.',
    long:
      'The IRS lets pretax 401(k) and Traditional IRA money grow tax-deferred — but it doesn\'t let it stay untouched forever. Starting at age 73 (75 if you were born in 1960 or later), you must withdraw a Required Minimum Distribution each year, and pay ordinary income tax on it.\n\nThe RMD amount = your prior-year-end account balance ÷ a divisor from the IRS Uniform Lifetime Table. At age 73 the divisor is about 26.5, so a $1,000,000 IRA owes roughly $37,700 the first year. The divisor shrinks as you age, so the percentage you must withdraw climbs over time.\n\nMissing an RMD used to trigger a 50% penalty; SECURE 2.0 lowered it to 25% (and 10% if corrected within two years). Roth IRAs and Roth 401(k)s have NO lifetime RMDs for the original owner.',
    links: [
      {
        label: 'IRS — Retirement topics: Required Minimum Distributions (RMDs)',
        href: 'https://www.irs.gov/retirement-plans/plan-participant-employee/retirement-topics-required-minimum-distributions-rmds',
      },
    ],
    related: ['irs_uniform_lifetime', 'pretax_401k', 'traditional_ira', 'roth_ira'],
    category: 'irs_limits',
  },
  {
    id: 'irs_uniform_lifetime',
    term: 'IRS Uniform Lifetime Table',
    aliases: ['uniform lifetime table', 'rmd table'],
    short:
      'The IRS divisor table used to compute the size of your Required Minimum Distribution each year.',
    long:
      'The Uniform Lifetime Table is the IRS\'s official lookup table of life-expectancy divisors, published in Treasury regulations. To calculate your RMD for a given year, divide your prior-year-end account balance by the divisor for your age that year.\n\nExample: at age 73 the divisor is 26.5, at 80 it\'s 20.2, at 90 it\'s 12.2. So the percentage of your account you\'re forced to withdraw rises as you age — roughly 3.8% at 73, around 5% at 80, around 8% at 90.\n\nThis table assumes a hypothetical beneficiary 10 years younger. If your sole beneficiary is a spouse more than 10 years younger, you use a different (larger) divisor table — meaning smaller RMDs.',
    links: [
      {
        label: 'IRS — Required Minimum Distribution worksheets',
        href: 'https://www.irs.gov/retirement-plans/plan-participant-employee/required-minimum-distribution-worksheets',
      },
    ],
    related: ['rmd'],
    category: 'irs_limits',
  },

  // ── Account types ────────────────────────────────────────────────────────
  {
    id: '401k',
    term: '401(k)',
    aliases: ['401k', '401(k) plan'],
    short:
      'An employer-sponsored retirement account. Employee contributions are typically pretax, reducing this year\'s taxable income.',
    long:
      'A 401(k) is a workplace retirement plan named after the section of the tax code that created it. You elect to defer a portion of each paycheck into the plan; the money goes in pretax (lowering your federal income tax for the year) and grows tax-deferred until withdrawal.\n\nMany 401(k)s also offer a Roth 401(k) sleeve — same paycheck deduction, but you pay tax now and the money grows tax-free forever. Some plans also offer an after-tax (non-Roth) sleeve, which is the foundation of the "mega backdoor Roth" strategy.\n\nThe IRS caps how much you can contribute each year — see 402(g) for your own deferral cap and 415(c) for the total cap including employer match and after-tax.',
    links: [
      {
        label: 'IRS — 401(k) Plans',
        href: 'https://www.irs.gov/retirement-plans/401k-plans',
      },
    ],
    related: ['pretax_401k', 'roth_401k', 'after_tax_401k', 'irs_402g', 'irs_415c'],
    category: 'account_types',
  },
  {
    id: 'pretax_401k',
    term: 'Pretax (Traditional) 401(k)',
    aliases: ['traditional 401k', 'pretax contribution'],
    short:
      'The classic 401(k) sleeve. Contributions reduce your taxable income now; withdrawals are taxed as ordinary income later.',
    long:
      'Pretax 401(k) contributions come out of your paycheck before federal income tax is calculated, so they reduce your taxable wages dollar-for-dollar in the current year. The money grows tax-deferred — no taxes on dividends or capital gains as it compounds.\n\nWhen you eventually withdraw (in retirement, ideally), every dollar comes out as ordinary income, taxed at whatever rates you face then. RMDs eventually force withdrawals starting at age 73.\n\nPretax is best when you expect to be in a LOWER tax bracket in retirement than you are today. If you expect to be in the SAME or higher bracket later, Roth often wins. Most plans let you split contributions between pretax and Roth.',
    links: [
      {
        label: 'IRS — Roth Comparison Chart',
        href: 'https://www.irs.gov/retirement-plans/roth-comparison-chart',
      },
    ],
    related: ['401k', 'roth_401k', 'rmd', 'roth_conversion'],
    category: 'account_types',
  },
  {
    id: 'roth_401k',
    term: 'Roth 401(k)',
    aliases: ['roth 401k', 'designated roth'],
    short:
      'A 401(k) sleeve where contributions are post-tax. Growth and qualified withdrawals are tax-free for life.',
    long:
      'A Roth 401(k) takes contributions out of your paycheck AFTER tax — you don\'t get a deduction this year. In return, the money grows tax-free, and qualified withdrawals (after age 59½ AND at least 5 years since your first Roth 401(k) contribution) come out tax-free.\n\nRoth 401(k) and pretax 401(k) share the same 402(g) employee deferral limit ($23,500 in 2026). You can split contributions across them in any ratio — many savers deliberately mix to diversify their future tax exposure.\n\nUnlike a Roth IRA, Roth 401(k) DOES have RMDs starting at 73 — but you can roll it to a Roth IRA in retirement to escape that requirement.',
    links: [
      {
        label: 'IRS — Designated Roth Accounts',
        href: 'https://www.irs.gov/retirement-plans/roth-acct-in-your-retirement-plan',
      },
    ],
    related: ['401k', 'pretax_401k', 'roth_ira', 'irs_402g'],
    category: 'account_types',
  },
  {
    id: 'after_tax_401k',
    term: 'After-tax 401(k)',
    aliases: ['after-tax 401(k)', 'non-roth after-tax'],
    short:
      'A 401(k) sleeve some plans offer beyond the 402(g) limit. Contributions are post-tax but growth is tax-deferred (not tax-free).',
    long:
      'After-tax 401(k) is a third sleeve some employer plans offer in addition to pretax and Roth. You contribute with post-tax dollars, but the contributions don\'t count against your 402(g) limit — they only count against the larger 415(c) total-additions limit ($70,000 in 2026).\n\nLeft alone, after-tax money grows TAX-DEFERRED (not tax-free) — your gains are eventually taxed as ordinary income. That\'s mediocre. The real magic happens when you immediately convert those after-tax dollars to Roth, either via in-service conversion or in-plan Roth rollover. That\'s the mega backdoor Roth.\n\nNot all employer plans offer after-tax contributions. Read your Summary Plan Description, or ask HR. The feature is most common at large tech companies.',
    links: [
      {
        label: 'IRS — Retirement Topics: After-Tax Contributions',
        href: 'https://www.irs.gov/retirement-plans/plan-participant-employee/retirement-topics-401k-and-profit-sharing-plan-contribution-limits',
      },
    ],
    related: ['mega_backdoor', 'irs_415c', 'irs_402g', 'roth_401k'],
    category: 'account_types',
  },
  {
    id: 'mega_backdoor',
    term: 'Mega backdoor Roth',
    aliases: ['mega backdoor', 'mbdr'],
    short:
      'A strategy to shovel after-tax 401(k) dollars into Roth, vastly more than the IRA Roth limits would normally allow.',
    long:
      'The "mega backdoor" leverages the gap between the 402(g) cap (~$23,500) and the 415(c) cap ($70,000) to get tens of thousands of EXTRA dollars into Roth each year — far beyond what a Roth IRA alone would allow.\n\nHow it works: contribute the maximum to your pretax or Roth 401(k) (hits 402(g)). Then make additional after-tax contributions up to whatever 415(c) headroom remains after employer match. Then immediately convert those after-tax dollars to Roth via "in-service conversion" or "in-plan Roth rollover." The contributions land in Roth at zero conversion cost (post-tax in, post-tax out); only the tiny amount of growth between deposit and conversion is taxable.\n\nRequires three things from your employer plan: (1) after-tax contributions allowed, (2) in-service distributions or in-plan conversions allowed, and (3) often automated rollovers to make it painless. If any are missing, the strategy is much harder or impossible.',
    links: [
      {
        label: 'Investopedia — Mega Backdoor Roth',
        href: 'https://www.investopedia.com/mega-backdoor-roth-ira-7559197',
      },
    ],
    related: ['after_tax_401k', 'irs_415c', 'irs_402g', 'roth_401k', 'backdoor_roth'],
    category: 'account_types',
  },
  {
    id: 'roth_ira',
    term: 'Roth IRA',
    aliases: ['roth individual retirement account'],
    short:
      'An individual retirement account funded with post-tax money. Growth and qualified withdrawals are tax-free.',
    long:
      'A Roth IRA is an individual (not employer-sponsored) retirement account where you contribute post-tax dollars. The money grows tax-free, and qualified withdrawals (after age 59½ AND at least 5 years since your first Roth IRA contribution) come out tax-free.\n\nThe 2026 contribution cap is $7,000 ($8,000 if you\'re 50+). High earners face income-based phase-outs — at high MAGI you can\'t contribute directly at all. The "backdoor Roth" workaround handles that.\n\nKey perks vs. Roth 401(k): no RMDs for the original owner, more flexible withdrawal of contributions (you can pull out original contributions tax/penalty-free at any time, just not the growth), and broader investment selection.',
    links: [
      {
        label: 'IRS — Roth IRAs',
        href: 'https://www.irs.gov/retirement-plans/roth-iras',
      },
    ],
    related: ['traditional_ira', 'backdoor_roth', 'ira_limit', 'roth_401k', 'magi'],
    category: 'account_types',
  },
  {
    id: 'traditional_ira',
    term: 'Traditional IRA',
    aliases: ['ira', 'individual retirement account'],
    short:
      'An individual retirement account. Contributions may be tax-deductible; growth is tax-deferred until withdrawal.',
    long:
      'A Traditional IRA is the original IRA. If you (and your spouse) don\'t have an employer retirement plan, contributions are fully deductible. If you do, the deduction phases out above certain income thresholds; above those, contributions are "non-deductible" (you put in post-tax dollars but tracked separately).\n\nGrowth is tax-deferred. Withdrawals are taxed as ordinary income, and RMDs kick in at age 73.\n\nThe 2026 cap is $7,000 ($8,000 with age-50 catch-up), shared with Roth IRA. Non-deductible Traditional IRA contributions are the first step in the backdoor Roth strategy.',
    links: [
      {
        label: 'IRS — Traditional IRAs',
        href: 'https://www.irs.gov/retirement-plans/traditional-iras',
      },
    ],
    related: ['roth_ira', 'backdoor_roth', 'ira_limit', 'rmd'],
    category: 'account_types',
  },
  {
    id: 'backdoor_roth',
    term: 'Backdoor Roth IRA',
    aliases: ['backdoor roth'],
    short:
      'A workaround that lets high-income earners get money into a Roth IRA even when income limits block direct contributions.',
    long:
      'Direct Roth IRA contributions phase out at higher incomes. The backdoor Roth sidesteps that by exploiting the fact that ANYONE can contribute to a Traditional IRA (non-deductibly) and ANYONE can convert Traditional IRA money to Roth.\n\nSteps: (1) contribute up to $7,000 to a Traditional IRA on a non-deductible basis. (2) Immediately convert the same amount to your Roth IRA. The conversion is tax-free because the basis (the amount you contributed post-tax) equals the amount converted.\n\nThe gotcha: the IRS\'s "pro-rata rule" — if you have OTHER pretax dollars in any Traditional IRA (or SEP/SIMPLE IRA) at year-end, the conversion is taxed proportionally. Solution: roll any pretax Traditional IRA money into your current 401(k) first if your plan accepts rollovers.',
    links: [
      {
        label: 'Investopedia — Backdoor Roth IRA',
        href: 'https://www.investopedia.com/terms/b/backdoor-roth-ira.asp',
      },
    ],
    related: ['roth_ira', 'traditional_ira', 'ira_limit', 'mega_backdoor', 'magi'],
    category: 'account_types',
  },
  {
    id: 'hysa',
    term: 'High-Yield Savings Account (HYSA)',
    aliases: ['high yield savings', 'high-yield savings account'],
    short:
      'A FDIC-insured savings account paying meaningfully more interest than a typical checking account. Fully liquid.',
    long:
      'A HYSA is just a savings account at a bank or credit union, but with an interest rate that actually keeps up with inflation (or close to it). Most pay 4–5% in periods of higher rates; less in low-rate periods. Insured up to $250,000 per depositor per bank by the FDIC (or NCUA for credit unions).\n\nHYSAs are best for emergency funds and short-term cash needs (1–3 years out). Beyond that, equity returns generally outpace HYSA yield over the long run.\n\nIn Cairn, your HYSA balance feeds the "HYSA bucket" used in projections — the engine assumes a real (after-inflation) return on it that\'s typically lower than the stock real return.',
    links: [
      {
        label: 'FDIC — Deposit Insurance',
        href: 'https://www.fdic.gov/resources/deposit-insurance/',
      },
    ],
    related: ['taxable_brokerage', 'bucket'],
    category: 'account_types',
  },
  {
    id: 'taxable_brokerage',
    term: 'Taxable brokerage',
    aliases: ['taxable account', 'brokerage account'],
    short:
      'A standard investment account with no contribution limits and no tax shelter. Dividends and capital gains are taxed annually.',
    long:
      'A taxable brokerage account holds stocks, bonds, ETFs, mutual funds, etc. with no contribution limits and no special tax treatment. You pay taxes on dividends and interest each year, and capital gains taxes when you sell appreciated holdings.\n\nThe upside: no early-withdrawal penalty (it\'s your money, accessible anytime), no RMDs, and long-term capital gains (assets held > 1 year) get preferential tax rates: 0%, 15%, or 20% depending on income.\n\nIn Cairn, the taxable brokerage bucket is the most flexible source for early-retirement spending and big purchases, and benefits most from tax-gain harvesting strategies.',
    links: [
      {
        label: 'Investopedia — Taxable vs. Tax-Deferred Investing',
        href: 'https://www.investopedia.com/terms/t/taxable-account.asp',
      },
    ],
    related: ['ltcg', 'stcg', 'bucket'],
    category: 'account_types',
  },

  // ── Tax concepts ─────────────────────────────────────────────────────────
  {
    id: 'magi',
    term: 'Modified Adjusted Gross Income (MAGI)',
    aliases: ['modified adjusted gross income'],
    short:
      'Your AGI plus certain add-backs. Used to determine eligibility for IRMAA, ACA subsidies, IRA contribution limits, and NIIT.',
    long:
      'MAGI is AGI with certain deductions added back — most commonly student loan interest, foreign earned income, and (depending on the test) tax-exempt interest. The IRS doesn\'t define one MAGI; each program (IRMAA, ACA, Roth IRA, NIIT) has its own slightly different version.\n\nMAGI matters because it gates key tax thresholds: above certain MAGI you can\'t contribute directly to a Roth IRA, your Medicare premiums get an IRMAA surcharge, your ACA subsidy shrinks, and you owe the 3.8% Net Investment Income Tax.\n\nIn Cairn, "MAGI" usually refers to the IRMAA flavor — AGI + tax-exempt interest. The engine uses current-year MAGI for IRMAA (a simplification — real IRMAA uses MAGI from two years prior).',
    links: [
      {
        label: 'IRS — Definition of Adjusted Gross Income',
        href: 'https://www.irs.gov/e-file-providers/definition-of-adjusted-gross-income',
      },
    ],
    related: ['agi', 'irmaa', 'aca', 'niit', 'roth_ira'],
    category: 'tax_concepts',
  },
  {
    id: 'agi',
    term: 'Adjusted Gross Income (AGI)',
    aliases: ['adjusted gross income'],
    short:
      'Gross income minus "above-the-line" deductions like 401(k) contributions and HSA contributions.',
    long:
      'AGI is the figure on Form 1040 line 11 — your gross income (wages, interest, dividends, business income, capital gains, etc.) minus a specific list of "above-the-line" deductions: pretax 401(k), HSA contributions, half of self-employment tax, deductible IRA contributions, student loan interest (up to a cap), and a few others.\n\nAGI is the starting point for most other tax calculations. From AGI you subtract either the standard deduction or itemized deductions to get Taxable Income, which is what your tax brackets actually apply to.\n\nMost MAGI calculations start with AGI and add specific items back.',
    links: [
      {
        label: 'IRS — Definition of Adjusted Gross Income',
        href: 'https://www.irs.gov/e-file-providers/definition-of-adjusted-gross-income',
      },
    ],
    related: ['magi', 'standard_deduction'],
    category: 'tax_concepts',
  },
  {
    id: 'marginal_rate',
    term: 'Marginal tax rate',
    aliases: ['marginal rate', 'marginal bracket'],
    short:
      'The tax rate on your NEXT dollar of income — i.e., the bracket you\'re currently in.',
    long:
      'The US has a progressive income tax: the first chunk of taxable income is taxed at 10%, the next chunk at 12%, then 22%, 24%, 32%, 35%, and finally 37% at the top. Your marginal rate is the bracket your last dollar lands in — the rate that would apply to one more dollar of income.\n\nMarginal rate matters for "what if I earn $X more?" decisions: a Roth conversion, a side gig, a bonus. If you\'re in the 24% bracket, a $1,000 conversion costs you $240 of federal tax (plus state).\n\nDON\'T confuse marginal with effective rate. Effective rate is your average — what you actually paid divided by your total income. It\'s always lower than marginal, because the lower brackets apply to most of your income.',
    links: [
      {
        label: 'IRS — Tax brackets',
        href: 'https://www.irs.gov/filing/federal-income-tax-rates-and-brackets',
      },
    ],
    related: ['effective_rate', 'bracket_headroom', 'roth_conversion'],
    category: 'tax_concepts',
  },
  {
    id: 'effective_rate',
    term: 'Effective tax rate',
    aliases: ['effective rate', 'average tax rate'],
    short:
      'Total tax paid divided by total income. Always lower than your marginal rate.',
    long:
      'Effective rate is a simple average: the total federal (or total all-in, depending on context) tax you owed, divided by your total income. Unlike the marginal rate, it accounts for the fact that lower brackets handle most of your income.\n\nExample: a 2026 MFJ filer with $200,000 of ordinary income falls in the 22% bracket — that\'s their marginal rate. But because the first $30,000 (standard deduction) isn\'t taxed and the remaining income is sliced across the 10%/12%/22% brackets, the effective federal rate is closer to 12–13%.\n\nWhen people say "I\'m in the X% bracket," they mean marginal. When they say "I paid X% in taxes," they mean effective.',
    links: [
      {
        label: 'IRS — Tax brackets',
        href: 'https://www.irs.gov/filing/federal-income-tax-rates-and-brackets',
      },
    ],
    related: ['marginal_rate'],
    category: 'tax_concepts',
  },
  {
    id: 'standard_deduction',
    term: 'Standard deduction',
    aliases: ['standard deduction'],
    short:
      'A flat dollar amount you can subtract from AGI before computing federal tax. Most filers take it instead of itemizing.',
    long:
      'The standard deduction is a no-questions-asked subtraction from AGI. For 2026 it\'s $30,000 for married-filing-jointly, $15,000 for single, and $22,500 for head of household. You can also itemize specific deductions (mortgage interest, SALT capped at $10K, charitable gifts, etc.) — you take whichever is larger.\n\nSince the Tax Cuts & Jobs Act doubled the standard deduction in 2018, roughly 90% of filers take the standard deduction rather than itemize.\n\nThis amount comes off the top of your AGI to compute Taxable Income, which is what your tax brackets are applied to.',
    currentValue: '$30,000 MFJ / $15,000 single (2026)',
    links: [
      {
        label: 'IRS — Standard Deduction',
        href: 'https://www.irs.gov/taxtopics/tc551',
      },
    ],
    related: ['agi', 'marginal_rate'],
    category: 'tax_concepts',
  },
  {
    id: 'ltcg',
    term: 'Long-Term Capital Gains (LTCG)',
    aliases: ['long term capital gains', 'long-term capital gains'],
    short:
      'Profits on assets held longer than one year. Taxed at preferential rates: 0%, 15%, or 20%.',
    long:
      'When you sell an investment for more than you paid, the profit is a capital gain. If you held it more than one year, it\'s a long-term capital gain and gets a preferential federal rate: 0%, 15%, or 20% depending on your taxable income.\n\nThe 0% bracket is the secret weapon of early retirees. In 2026, MFJ filers can realize up to ~$98,900 of LTCG tax-free if they have low ordinary income (the 0% bracket runs up to that amount of taxable income, including the LTCG itself). This enables "tax-gain harvesting" — deliberately selling appreciated assets in low-income years to reset your cost basis tax-free.\n\nLTCG stacks ON TOP of ordinary income; they don\'t share brackets but the LTCG bracket calculation considers your ordinary income as the floor.',
    links: [
      {
        label: 'IRS — Topic No. 409 Capital Gains and Losses',
        href: 'https://www.irs.gov/taxtopics/tc409',
      },
    ],
    related: ['stcg', 'taxable_brokerage', 'niit'],
    category: 'tax_concepts',
  },
  {
    id: 'stcg',
    term: 'Short-Term Capital Gains (STCG)',
    aliases: ['short term capital gains'],
    short:
      'Profits on assets held one year or less. Taxed as ordinary income — no preferential rate.',
    long:
      'A short-term capital gain is a profit on an asset you held for one year or less. Unlike long-term gains, short-term gains get NO preferential rate — they\'re lumped in with your ordinary income and taxed at your marginal income rate (10% to 37%).\n\nThis is why the "one year and one day" holding period matters so much. Selling a winning trade at 364 days vs. 366 days can be the difference between a 32% federal tax and a 15% federal tax.\n\nLosses follow the same logic — short-term losses offset short-term gains first, long-term losses offset long-term gains first.',
    links: [
      {
        label: 'IRS — Topic No. 409 Capital Gains and Losses',
        href: 'https://www.irs.gov/taxtopics/tc409',
      },
    ],
    related: ['ltcg', 'marginal_rate'],
    category: 'tax_concepts',
  },
  {
    id: 'niit',
    term: 'Net Investment Income Tax (NIIT)',
    aliases: ['niit', 'net investment income tax', '3.8% surtax'],
    short:
      'A 3.8% surtax on investment income (dividends, interest, capital gains) above MAGI thresholds.',
    long:
      'IRC §1411 imposes an extra 3.8% tax on net investment income (interest, dividends, capital gains, rental income, etc.) when your MAGI exceeds $250,000 (MFJ) or $200,000 (single). It applies to the LESSER of (a) your net investment income, or (b) the amount your MAGI exceeds the threshold.\n\nExample: MFJ with $300,000 MAGI and $40,000 of dividends. MAGI excess = $50,000; net investment income = $40,000; NIIT = 3.8% × $40,000 = $1,520.\n\nNIIT is on TOP of regular capital gains tax. So a high earner in the 20% LTCG bracket effectively pays 23.8% federal on long-term gains, plus state.',
    links: [
      {
        label: 'IRS — Net Investment Income Tax',
        href: 'https://www.irs.gov/individuals/net-investment-income-tax',
      },
    ],
    related: ['magi', 'ltcg', 'additional_medicare'],
    category: 'tax_concepts',
  },
  {
    id: 'fica',
    term: 'FICA (Social Security + Medicare)',
    aliases: ['fica', 'payroll tax'],
    short:
      'The 7.65% payroll tax on wages: 6.2% for Social Security (capped) + 1.45% for Medicare (uncapped).',
    long:
      'The Federal Insurance Contributions Act tax funds Social Security and Medicare. Employees pay 6.2% Social Security on wages up to the Social Security wage base ($176,100 in 2026), plus 1.45% Medicare on all wages — total 7.65%. Employers match this exactly, so the program collects 15.3% per dollar of wages.\n\nSelf-employed people pay both halves themselves (15.3%) but get to deduct the employer-equivalent half from AGI.\n\nFor wages above $200,000 (single) or $250,000 (MFJ), there\'s an Additional Medicare Tax of 0.9% — see that entry. Wages above the SS wage base only owe the 1.45% Medicare portion.',
    links: [
      {
        label: 'IRS — Topic No. 751 Social Security and Medicare Withholding Rates',
        href: 'https://www.irs.gov/taxtopics/tc751',
      },
    ],
    related: ['additional_medicare', 'social_security'],
    category: 'tax_concepts',
  },
  {
    id: 'additional_medicare',
    term: 'Additional Medicare Tax (0.9%)',
    aliases: ['additional medicare tax', 'high earner medicare'],
    short:
      'An extra 0.9% Medicare tax on wages above $250,000 MFJ ($200,000 single).',
    long:
      'On top of the regular 1.45% Medicare tax that applies to all wages, high earners owe an additional 0.9% on wages above $250,000 (MFJ) or $200,000 (single). Unlike the regular Medicare tax, the additional 0.9% is only owed by the employee — there\'s no employer match.\n\nEmployers withhold the additional 0.9% on wages above $200,000 from any single employer, regardless of filing status. If you\'re married and both spouses earn but neither alone hits $200K, you may owe it on your joint return even though nothing was withheld during the year.\n\nThis is a separate tax from NIIT (which targets investment income at the same MAGI thresholds). High earners with both wages and investment income often owe both.',
    links: [
      {
        label: 'IRS — Questions and Answers for the Additional Medicare Tax',
        href: 'https://www.irs.gov/businesses/small-businesses-self-employed/questions-and-answers-for-the-additional-medicare-tax',
      },
    ],
    related: ['fica', 'niit'],
    category: 'tax_concepts',
  },
  {
    id: 'roth_conversion',
    term: 'Roth conversion',
    aliases: ['roth conversion', 'in-plan roth conversion'],
    short:
      'Moving pretax retirement dollars to Roth. You pay ordinary income tax on the converted amount now in exchange for tax-free growth forever.',
    long:
      'A Roth conversion takes money from a pretax 401(k) or Traditional IRA and moves it into a Roth account. The converted amount is treated as ordinary income for the year — you owe federal (and usually state) tax on it now. In return, that money grows tax-free for life and has no future RMDs (in a Roth IRA).\n\nConversions are most attractive in low-income years: gap years between leaving a job and Social Security/RMDs starting, or sabbaticals. Common strategy: "fill up" your current bracket each year by converting just enough to stay below the next bracket threshold.\n\nKey rules: there\'s no contribution limit on conversions (you can convert $500K in a single year if you can stomach the tax bill). After a Roth conversion, the converted amount has its OWN 5-year clock before it can be withdrawn penalty-free if you\'re under 59½.',
    links: [
      {
        label: 'IRS — Rollovers of Retirement Plan and IRA Distributions',
        href: 'https://www.irs.gov/retirement-plans/plan-participant-employee/rollovers-of-retirement-plan-and-ira-distributions',
      },
    ],
    related: ['pretax_401k', 'roth_ira', 'traditional_ira', 'bracket_headroom', 'rmd'],
    category: 'tax_concepts',
  },
  {
    id: 'bracket_headroom',
    term: 'Bracket headroom',
    aliases: ['headroom', 'bracket fill'],
    short:
      'How much more ordinary income you could realize before crossing into the next-higher tax bracket.',
    long:
      'Bracket headroom is the dollar amount you could add to your taxable ordinary income this year before pushing into the next tax bracket. It\'s the practical input for "how big should my Roth conversion be?" — typically you want to convert just enough to fill the current bracket without spilling into the next.\n\nExample: 2026 MFJ filer with $80,000 of taxable income is in the 12% bracket (which runs up to ~$96,950). Headroom to the 22% bracket is roughly $16,950 — convert that much pretax money to Roth and pay only 12% on it.\n\nCairn\'s engine surfaces headroom as part of its tax view, especially during retirement years where it can identify tax-gain harvesting and Roth-conversion opportunities.',
    links: [
      {
        label: 'IRS — Tax brackets',
        href: 'https://www.irs.gov/filing/federal-income-tax-rates-and-brackets',
      },
    ],
    related: ['marginal_rate', 'roth_conversion', 'ltcg'],
    category: 'tax_concepts',
  },

  // ── Medicare & ACA ────────────────────────────────────────────────────────
  {
    id: 'irmaa',
    term: 'IRMAA (Income-Related Monthly Adjustment)',
    aliases: ['irmaa', 'medicare premium surcharge'],
    short:
      'A Medicare Part B and Part D premium surcharge that high-income retirees pay. Determined by MAGI from two years ago.',
    long:
      'IRMAA is the Income-Related Monthly Adjustment Amount — an extra fee on top of standard Medicare Part B and Part D premiums for high-income beneficiaries. Tiers start above $206,000 MAGI for MFJ ($103,000 single) and climb in five steps.\n\nCMS uses your MAGI from TWO YEARS AGO to set this year\'s premium. So your 2026 premium is based on your 2024 tax return. If your income drops sharply (retirement, job loss), you can file Form SSA-44 to ask for a "life-changing event" reconsideration.\n\nIRMAA is a "cliff" — crossing a tier boundary by even $1 of MAGI bumps your annual cost by hundreds or thousands. This makes the year before you turn 63 (the lookback for age-65 IRMAA) a critical income-management year for early retirees.',
    links: [
      {
        label: 'CMS — Medicare Costs',
        href: 'https://www.medicare.gov/basics/costs/medicare-costs',
      },
      {
        label: 'SSA — Medicare Income-Related Premium',
        href: 'https://www.ssa.gov/benefits/medicare/medicare-premiums.html',
      },
    ],
    related: ['magi', 'medicare_part_b', 'medicare_part_d', 'medicare'],
    category: 'medicare_aca',
  },
  {
    id: 'aca',
    term: 'Affordable Care Act (ACA)',
    aliases: ['aca', 'obamacare'],
    short:
      'The federal healthcare marketplace. Critical for early retirees who need health insurance before Medicare at 65.',
    long:
      'The Affordable Care Act created Healthcare.gov (and state-run marketplaces) where individuals can buy health insurance not tied to an employer. For early retirees — anyone retiring before 65 — the ACA marketplace is typically the bridge to Medicare.\n\nThe ACA also offers premium tax credits on a sliding scale based on your MAGI relative to the Federal Poverty Level (FPL). Households at 100–400% of FPL get the most subsidy; the Inflation Reduction Act extended subsidies above 400% (capped at 8.5% of MAGI) through 2025 — check current rules.\n\nEarly retirees often deliberately structure their MAGI to maximize ACA subsidies — keeping income low enough to qualify, often through Roth conversions in just-the-right amounts and drawing primarily from already-taxed sources (HYSA, taxable brokerage basis).',
    links: [
      {
        label: 'Healthcare.gov — Get coverage',
        href: 'https://www.healthcare.gov/',
      },
    ],
    related: ['aca_premium_credit', 'magi', 'medicare'],
    category: 'medicare_aca',
  },
  {
    id: 'aca_premium_credit',
    term: 'ACA premium tax credit',
    aliases: ['premium tax credit', 'aca subsidy'],
    short:
      'A sliding-scale federal subsidy that reduces your ACA marketplace health insurance premium based on MAGI.',
    long:
      'The Premium Tax Credit (PTC) is the federal subsidy that makes ACA marketplace plans affordable for low and middle-income households. It\'s sized based on your MAGI relative to the Federal Poverty Level (FPL) for your household size — the lower your MAGI, the larger the subsidy.\n\nThe credit is generally calculated to cap your premium for a benchmark "silver" plan at a certain percent of MAGI — ranging from ~0% at 150% FPL up to 8.5% above 400% FPL (under temporary IRA rules through 2025; check current law).\n\nYou typically receive the credit in advance (lower monthly premium); at tax time you reconcile actual MAGI vs. estimated. Underestimating MAGI means owing back excess advance credits at filing.',
    links: [
      {
        label: 'Healthcare.gov — Premium tax credit',
        href: 'https://www.healthcare.gov/glossary/premium-tax-credit/',
      },
    ],
    related: ['aca', 'magi'],
    category: 'medicare_aca',
  },
  {
    id: 'medicare',
    term: 'Medicare',
    aliases: ['medicare'],
    short:
      'Federal health insurance for people 65+ (and some under 65 with disabilities).',
    long:
      'Medicare is the federal health insurance program covering most Americans starting at age 65. It has four parts: Part A (hospital, usually free), Part B (outpatient/doctors, monthly premium), Part C (Medicare Advantage, private alternative bundling A+B+D), and Part D (prescription drugs, monthly premium).\n\nYou should enroll in Part A and Part B during the seven-month "Initial Enrollment Period" around your 65th birthday — late enrollment penalties exist and add up over time. Part B and Part D premiums are subject to IRMAA surcharges for high-income retirees.\n\nMedicare doesn\'t cover everything — most retirees buy a "Medigap" supplement plan or choose Medicare Advantage to handle the gaps and out-of-pocket exposure.',
    links: [
      {
        label: 'Medicare.gov',
        href: 'https://www.medicare.gov/',
      },
    ],
    related: ['medicare_part_b', 'medicare_part_d', 'irmaa', 'aca'],
    category: 'medicare_aca',
  },
  {
    id: 'medicare_part_b',
    term: 'Medicare Part B',
    aliases: ['part b'],
    short:
      'Medicare\'s outpatient/doctor coverage. Charges a monthly premium that\'s higher for high-income retirees (IRMAA).',
    long:
      'Medicare Part B covers doctor visits, outpatient procedures, durable medical equipment, lab tests, and preventive services. Most enrollees pay the standard monthly premium ($185 in 2025; check Medicare.gov for current year). Higher-income retirees pay more via IRMAA surcharges on top of the standard premium.\n\nLate-enrollment penalty: if you delay Part B without other "creditable" coverage, your monthly premium permanently increases by 10% for each full year you were eligible but didn\'t enroll.',
    links: [
      {
        label: 'Medicare.gov — Part B Costs',
        href: 'https://www.medicare.gov/basics/costs/medicare-costs',
      },
    ],
    related: ['medicare', 'irmaa'],
    category: 'medicare_aca',
  },
  {
    id: 'medicare_part_d',
    term: 'Medicare Part D',
    aliases: ['part d'],
    short:
      'Medicare\'s prescription drug coverage. Sold by private insurers, with IRMAA surcharges at high incomes.',
    long:
      'Medicare Part D is prescription drug coverage. It\'s sold by private insurers with plan designs and formularies that vary widely; the monthly base premium varies but a separate IRMAA surcharge is added on top for high-income retirees.\n\nLike Part B, late enrollment without creditable coverage triggers a permanent monthly penalty (1% of the national base premium per month of delay).\n\nThe Inflation Reduction Act (2022) introduced a $2,000 annual out-of-pocket cap for Part D drugs starting in 2025 — a major change from the previous catastrophic-only design.',
    links: [
      {
        label: 'Medicare.gov — Part D Drug Coverage',
        href: 'https://www.medicare.gov/drug-coverage-part-d',
      },
    ],
    related: ['medicare', 'irmaa'],
    category: 'medicare_aca',
  },

  // ── Social Security ──────────────────────────────────────────────────────
  {
    id: 'social_security',
    term: 'Social Security',
    aliases: ['social security', 'ss', 'ssa'],
    short:
      'Federal retirement benefit funded by FICA payroll taxes. Claimable starting at age 62; bigger benefit if you wait longer.',
    long:
      'Social Security pays a monthly retirement benefit based on your 35 highest-earning years (indexed for wage inflation). You can start claiming as early as 62 with a permanent reduction (~30% less than your "full" benefit), at Full Retirement Age (66 to 67 depending on birth year) for the standard amount, or delay up to age 70 for an 8% per-year boost.\n\nFor most singles in average health, delaying to 70 maximizes lifetime expected value if you live past your mid-80s. For couples, the higher earner often delays (because the survivor inherits the larger benefit) while the lower earner claims earlier.\n\nUp to 85% of your SS benefits may be taxable as ordinary income depending on your "provisional income" — see that entry.',
    links: [
      {
        label: 'SSA — When to start receiving retirement benefits',
        href: 'https://www.ssa.gov/benefits/retirement/learn.html',
      },
    ],
    related: ['provisional_income', 'fica'],
    category: 'social_security',
  },
  {
    id: 'provisional_income',
    term: 'Provisional income',
    aliases: ['combined income', 'ss provisional income'],
    short:
      'A figure used to determine how much of your Social Security is taxable. = AGI + 50% of SS benefits + tax-exempt interest.',
    long:
      'Provisional income (sometimes called "combined income") is the IRS\'s lookup key for Social Security taxation. It\'s calculated as: AGI (excluding SS) + tax-exempt interest + 50% of your Social Security benefits.\n\nFor MFJ filers in 2026: if provisional income is below $32,000, none of your SS is taxable. Between $32,000 and $44,000, up to 50% of SS becomes taxable. Above $44,000, up to 85% of SS becomes taxable. (Single thresholds: $25,000 / $34,000.) These thresholds have NOT been adjusted for inflation since 1984 — meaning more retirees fall above them every year.\n\nThis is why claiming SS while still earning high income or doing big Roth conversions can multiply your effective marginal rate — pushing more of your benefit into taxability while also taxing the income directly.',
    links: [
      {
        label: 'SSA — Income taxes and your Social Security benefit',
        href: 'https://www.ssa.gov/benefits/retirement/planner/taxes.html',
      },
    ],
    related: ['social_security', 'agi', 'magi'],
    category: 'social_security',
  },

  // ── Mortgage & loans ─────────────────────────────────────────────────────
  {
    id: 'mortgage_pi',
    term: 'Principal & Interest (P&I)',
    aliases: ['p&i', 'principal and interest'],
    short:
      'The portion of a mortgage payment that pays down principal and accrues interest. Excludes escrow.',
    long:
      'Your monthly mortgage payment typically has two big buckets: P&I (principal + interest) and escrow (property tax + homeowners insurance, sometimes PMI). P&I is the part that goes to the lender for the loan itself.\n\nIn the early years of a 30-year mortgage, almost all of P&I is interest — only a small slice pays down principal. The mix flips over time. For a 30-year fixed at 6.5%, you typically don\'t cross the "more principal than interest" threshold until around year 18.\n\nWhen the loan is fully paid off, P&I goes away — but escrow (property tax + insurance) continues for as long as you own the home.',
    links: [
      {
        label: 'CFPB — What is a mortgage payment?',
        href: 'https://www.consumerfinance.gov/owning-a-home/process/loan/',
      },
    ],
    related: ['escrow', 'amortization', 'refinance'],
    category: 'mortgage_loans',
  },
  {
    id: 'escrow',
    term: 'Escrow (mortgage)',
    aliases: ['escrow account', 'impound account'],
    short:
      'A holding account your lender uses to pay your property taxes and homeowners insurance on your behalf.',
    long:
      'Most mortgages bundle property taxes and homeowners insurance (and sometimes PMI) into your monthly payment. The lender holds those dollars in an "escrow" account and pays the bills when due — typically tax bills twice a year, insurance annually.\n\nEscrow keeps your monthly payment predictable and protects the lender from unpaid taxes (which could become a senior lien on the property). If your tax assessment or insurance premium goes up, the lender raises your monthly escrow contribution — which is why your "fixed-rate" mortgage payment can still grow over time.\n\nWhen the underlying loan is paid off, escrow can stop — but you\'re then responsible for paying tax and insurance bills directly, on the real schedule, in larger lump sums.',
    links: [
      {
        label: 'CFPB — What is an escrow account?',
        href: 'https://www.consumerfinance.gov/ask-cfpb/what-is-an-escrow-or-impound-account-en-140/',
      },
    ],
    related: ['mortgage_pi'],
    category: 'mortgage_loans',
  },
  {
    id: 'refinance',
    term: 'Refinance',
    aliases: ['refi', 'refinancing'],
    short:
      'Replacing your existing mortgage with a new one — different rate, term, or both.',
    long:
      'Refinancing pays off your current mortgage with proceeds from a new loan. Common reasons: lower interest rate, shorter term to pay off faster, longer term to lower monthly payment, switch from ARM to fixed, or "cash-out refi" to pull home equity as cash.\n\nClosing costs (typically 2–5% of the loan amount) eat into refi savings. The rule of thumb is "break-even" — calculate how many months of monthly-payment savings it takes to recoup closing costs. If you plan to keep the home longer than that, refinancing pencils out.\n\nIn Cairn, the `refinance` event replaces your current P&I and amortization schedule with a new loan amount, rate, and term — Cairn handles the math.',
    links: [
      {
        label: 'CFPB — Refinancing',
        href: 'https://www.consumerfinance.gov/owning-a-home/loan-options/refinance/',
      },
    ],
    related: ['mortgage_pi', 'amortization'],
    category: 'mortgage_loans',
  },
  {
    id: 'amortization',
    term: 'Amortization',
    aliases: ['amortization schedule', 'amortizing loan'],
    short:
      'The schedule by which a loan\'s principal is paid down over its term. Each payment splits between interest and principal.',
    long:
      'An "amortizing" loan is one where each scheduled payment is the same dollar amount, with each payment\'s split between interest and principal calculated to fully retire the loan by the end of the term.\n\nFor a 30-year mortgage, the early payments are mostly interest (the loan balance is highest, so the same rate produces more interest dollars). As principal slowly drops, each payment\'s interest share shrinks and the principal share grows. By the last few years, almost every dollar is principal.\n\nThis front-loaded interest pattern is why "extra payments early" save more total interest than the same extra dollars paid late — you\'re cutting principal that would otherwise compound interest for many remaining years.',
    links: [
      {
        label: 'CFPB — How does amortization work?',
        href: 'https://www.consumerfinance.gov/ask-cfpb/what-is-amortization-and-how-could-it-affect-my-auto-loan-en-845/',
      },
    ],
    related: ['mortgage_pi', 'refinance'],
    category: 'mortgage_loans',
  },
  {
    id: 'heloc',
    term: 'Home Equity Line of Credit (HELOC)',
    aliases: ['heloc', 'home equity line'],
    short:
      'A revolving credit line secured by your home equity. Variable rate. You borrow as needed and pay interest only on the balance.',
    long:
      'A HELOC is like a credit card secured by your house. The bank gives you a credit limit (typically up to 80–85% of home value minus your existing mortgage). You can draw on it as you need, and only pay interest on what you\'ve borrowed.\n\nHELOC rates are usually variable (tied to prime rate). They\'re typically lower than personal loan or credit card rates because the home is collateral — but that also means you can lose the home if you can\'t pay.\n\nCommon uses: home renovations (interest may be tax-deductible if used to "buy, build, or substantially improve" the home), bridge loans, or as an emergency credit reserve. Bad uses: vacations, cars, anything with no lasting value — you\'re putting the house up for things that depreciate.',
    links: [
      {
        label: 'CFPB — What is a HELOC?',
        href: 'https://www.consumerfinance.gov/ask-cfpb/what-is-a-home-equity-loan-en-106/',
      },
    ],
    related: ['mortgage_pi', '401k_loan'],
    category: 'mortgage_loans',
  },
  {
    id: 'section_121',
    term: '§121 home-sale exclusion',
    aliases: ['section 121', '121 exclusion', 'home sale exclusion'],
    short:
      'IRC §121 lets you exclude up to $250K (single) or $500K (MFJ) of capital gains when you sell your primary residence.',
    long:
      'When you sell your primary residence, IRS §121 lets you exclude up to $250,000 of capital gain ($500,000 if married filing jointly) from federal taxes. To qualify, you must have owned AND lived in the home as your principal residence for at least 2 of the last 5 years.\n\nAny gain above the exclusion is taxed as long-term capital gains. Example: bought for $400K, sold for $1.2M, MFJ — total gain is $800K. $500K is excluded; the remaining $300K is LTCG.\n\nYou can only use §121 once every two years. It\'s arguably the largest tax-free wealth event most American households ever realize, which is why "moving to a less expensive area" can be a powerful financial event in retirement.',
    links: [
      {
        label: 'IRS — Topic No. 701 Sale of Your Home',
        href: 'https://www.irs.gov/taxtopics/tc701',
      },
    ],
    related: ['ltcg'],
    category: 'mortgage_loans',
  },
  {
    id: '401k_loan',
    term: '401(k) loan',
    aliases: ['401k loan', 'retirement plan loan'],
    short:
      'A loan from your own 401(k) balance. You pay it back to yourself with interest — but principal is out of the market while borrowed.',
    long:
      'Many 401(k) plans allow loans up to 50% of your vested balance, capped at $50,000. You repay through payroll deduction over up to 5 years (or longer for a primary-home purchase). The interest you pay goes back into your own account.\n\nUpsides: no credit check, fast access, low effective cost (interest paid to yourself). Downsides: the borrowed principal is OUT of the market until repaid — if markets rip, you miss the gains. And if you leave your job, the loan typically has to be repaid quickly or it becomes a deemed distribution (taxes + penalty if you\'re under 59½).\n\nIn Cairn\'s funding model, a 401(k) loan pulls principal from the retirement bucket at origination, then credits principal back as the loan repays — and is NOT counted as a net-worth liability (you owe it to yourself).',
    links: [
      {
        label: 'IRS — Retirement Topics: Plan Loans',
        href: 'https://www.irs.gov/retirement-plans/plan-participant-employee/retirement-topics-plan-loans',
      },
    ],
    related: ['401k', 'pretax_401k', 'heloc'],
    category: 'mortgage_loans',
  },

  // ── Retirement planning ──────────────────────────────────────────────────
  {
    id: 'swr',
    term: 'Safe Withdrawal Rate (SWR)',
    aliases: ['safe withdrawal rate', '4% rule'],
    short:
      'The annual % of your portfolio you can withdraw and historically not run out of money. The "4% rule" is shorthand.',
    long:
      'SWR is the percentage of your retirement portfolio you can spend each year (adjusted for inflation) such that you historically wouldn\'t have run out over a long retirement. The famous 1998 "Trinity Study" found 4% supported a 30-year retirement in nearly all historical 30-year windows, hence the "4% rule."\n\nFor longer retirements (FIRE-style 50+ years), the conventional SWR drops to ~3.0–3.5%. Recent research suggests 3.25% as a defensible long-horizon rate. Cairn defaults to a configurable SWR.\n\nSWR is a planning heuristic, not a guarantee. Real retirees use dynamic strategies (spend less in down years, more in up years), variable percentage withdrawals, or "guardrails" to adapt — which often allow higher initial spending than a fixed-SWR plan.',
    links: [
      {
        label: 'Wikipedia — Trinity Study',
        href: 'https://en.wikipedia.org/wiki/Trinity_study',
      },
      {
        label: 'Investopedia — Safe Withdrawal Rate',
        href: 'https://www.investopedia.com/terms/s/safe-withdrawal-rate-swr-method.asp',
      },
    ],
    related: ['nest_egg', 'sequence_of_returns_risk', 'real_return'],
    category: 'retirement_planning',
  },
  {
    id: 'nest_egg',
    term: 'Nest egg',
    aliases: ['nest egg', 'retirement portfolio'],
    short:
      'Your total investable portfolio at retirement — the pile of money you live off of.',
    long:
      'A nest egg is the total balance of investable assets — HYSA, taxable brokerage, 401(k), IRA, Roth IRA — at the moment you retire. It\'s the input to the SWR calculation: nest egg × SWR ≈ sustainable annual withdrawal in real dollars.\n\nIt typically excludes home equity (you can\'t spend a kitchen) and ongoing income streams like pensions or Social Security (those reduce required spending separately). Some planners include a portion of home equity if downsizing is part of the plan.\n\nIn Cairn, the projected nest egg appears on the dashboard as the engine\'s estimate of your investable balance at your target retirement age.',
    links: [
      {
        label: 'Investopedia — Nest Egg',
        href: 'https://www.investopedia.com/terms/n/nestegg.asp',
      },
    ],
    related: ['swr', 'real_return', 'bucket'],
    category: 'retirement_planning',
  },
  {
    id: 'real_return',
    term: 'Real return (inflation-adjusted)',
    aliases: ['real return', 'inflation-adjusted return'],
    short:
      'Investment return after subtracting inflation. Cairn\'s entire projection works in real (today\'s) dollars.',
    long:
      'A "real" return is what your money actually grew in purchasing power, after accounting for inflation. If your portfolio grows 9% but inflation is 3%, your real return is approximately 6%.\n\nReal return is the right number to use for long-term planning because it lets you reason in today\'s dollars throughout. Cairn does this by design: every dollar in the projection — balances, contributions, withdrawals, home value, mortgage P&I — is in today\'s dollars. There are no inflation-inflated numbers anywhere in the chart or table.\n\nThis means the "$50,000 retirement spend in 30 years" you see in Cairn is $50,000 of TODAY\'s buying power, not the nominal $121,000 it would actually take in 2056 dollars at 3% inflation.',
    links: [
      {
        label: 'Investopedia — Real Rate of Return',
        href: 'https://www.investopedia.com/terms/r/realrateofreturn.asp',
      },
    ],
    related: ['swr', 'nest_egg'],
    category: 'retirement_planning',
  },
  {
    id: 'sequence_of_returns_risk',
    term: 'Sequence of returns risk',
    aliases: ['sequence risk', 'sorr'],
    short:
      'The risk that bad early-retirement returns + ongoing withdrawals exhaust a portfolio that on average should have lasted.',
    long:
      'Sequence-of-returns risk (SORR) is the cruel reality that the ORDER of returns matters when you\'re drawing from a portfolio — even if the long-run average is fine. A 30-year retirement that starts with a 40% drawdown can run out of money even if the average annual return is identical to one that starts with a great decade.\n\nWhy: when your balance is lower, withdrawals carve out a bigger percentage. Selling shares at depressed prices for living expenses leaves fewer shares to enjoy the recovery.\n\nDefenses include: holding 1–3 years of cash/bonds in a "bond tent" near retirement, dynamic withdrawal strategies (spend less when markets are down), Roth conversion buffers, deferring Social Security to provide a higher floor income, and not retiring at the very peak of a long bull market without a margin of safety.',
    links: [
      {
        label: 'Investopedia — Sequence Risk',
        href: 'https://www.investopedia.com/terms/s/sequence-risk.asp',
      },
    ],
    related: ['swr', 'nest_egg'],
    category: 'retirement_planning',
  },
  {
    id: 'coast_fire',
    term: 'Coast FIRE',
    aliases: ['coast fire', 'coast'],
    short:
      'A FIRE variant: save enough early so compounding alone gets you to retirement; "coast" with just enough income to cover today\'s expenses.',
    long:
      'Coast FIRE means you\'ve invested enough that, even if you stopped saving entirely, normal market returns would grow it into a sufficient nest egg by your target retirement age. After hitting Coast FIRE, you no longer NEED to save — you just need to cover current living costs.\n\nThis frees you to take a less stressful job, work part-time, change careers, take a sabbatical, or work for passion rather than salary. Many Coast FIRE practitioners still save when they can, but they\'re no longer forced to grind.\n\nThe Coast number is much smaller than full FIRE because it relies on decades of compounding to do the heavy lifting. A 30-year-old who needs $1M at 65 has only a ~$155K Coast number assuming 6% real returns over 35 years.',
    links: [
      {
        label: 'Investopedia — Coast FIRE',
        href: 'https://www.investopedia.com/coast-fire-7104190',
      },
    ],
    related: ['swr', 'nest_egg', 'barista_fire', 'lean_fire', 'fat_fire'],
    category: 'retirement_planning',
  },
  {
    id: 'barista_fire',
    term: 'Barista FIRE',
    aliases: ['barista fire'],
    short:
      'A FIRE variant: partial retirement covered by part-time work + investments. The job typically supplies health insurance.',
    long:
      'Barista FIRE refers to a plan where you semi-retire early — covering some of your living expenses with a low-stress part-time job (the metaphorical "barista at Starbucks" gig) and the rest with investment withdrawals.\n\nThe critical perk often isn\'t the wages, it\'s health insurance. Many large employers offer benefits to part-time workers — Starbucks famously does — bridging the painful gap between leaving a career and Medicare at 65.\n\nThe required nest egg is much smaller than full FIRE because the part-time income reduces the SWR draw needed. Many Coast FIRE practitioners eventually transition to Barista FIRE.',
    links: [
      {
        label: 'Investopedia — Barista FIRE',
        href: 'https://www.investopedia.com/barista-fire-8404330',
      },
    ],
    related: ['coast_fire', 'lean_fire', 'fat_fire', 'aca'],
    category: 'retirement_planning',
  },
  {
    id: 'lean_fire',
    term: 'Lean FIRE',
    aliases: ['lean fire'],
    short:
      'Retirement on a deliberately small annual spend — typically $25k–$50k/yr in today\'s dollars.',
    long:
      'Lean FIRE describes the philosophy of retiring on a much smaller annual spend than the "average American household" — typically $25,000 to $50,000 per year, often achieved through geographic arbitrage (low cost-of-living area), debt-free living, simple lifestyles, and minimalism.\n\nThe upside: the required nest egg is much smaller (e.g., a $40,000/yr spend at 4% SWR = $1M nest egg, vs. $2M+ for a $80k spend). Many Lean FIRE practitioners reach financial independence in their 30s.\n\nThe downsides: less margin for unexpected expenses, less flexibility for travel or hobbies, and higher exposure to inflation (a smaller buffer above your minimum spend means smaller % moves matter more).',
    links: [
      {
        label: 'Investopedia — Lean FIRE',
        href: 'https://www.investopedia.com/lean-fire-8404326',
      },
    ],
    related: ['coast_fire', 'barista_fire', 'fat_fire', 'swr'],
    category: 'retirement_planning',
  },
  {
    id: 'fat_fire',
    term: 'Fat FIRE',
    aliases: ['fat fire'],
    short:
      'Retirement on a higher-than-average annual spend — comfortable lifestyle, often $150k+/yr.',
    long:
      'Fat FIRE refers to early retirement at a spend level comfortably above average — often $150,000 to $250,000+ per year. It implies a much larger required nest egg ($4M to $6M+ at 4% SWR) and typically a longer working career and/or higher savings rate.\n\nFat FIRE retirees prioritize lifestyle continuity (or improvement) over hitting the financial-independence finish line as fast as possible. This often suits high-income professionals — doctors, tech workers, lawyers — who can save very large amounts in their peak earning years.\n\nThe larger nest egg also provides robust margin against sequence-of-returns risk, lifestyle inflation, big unplanned expenses (eldercare, kids\' college), and gives more flexibility to absorb a bear market without changing spending.',
    links: [
      {
        label: 'Investopedia — Fat FIRE',
        href: 'https://www.investopedia.com/fat-fire-8404322',
      },
    ],
    related: ['coast_fire', 'lean_fire', 'barista_fire', 'swr'],
    category: 'retirement_planning',
  },

  // ── Cairn-specific terms ─────────────────────────────────────────────────
  {
    id: 'bucket',
    term: 'Bucket (Cairn)',
    aliases: ['bucket', 'investable bucket'],
    short:
      'A Cairn category of investable money: HYSA, Taxable, or Retirement. Drives projection math.',
    long:
      'Cairn groups every investable account into one of three "buckets" the engine reasons over: HYSA (savings, low real return, fully liquid, no tax friction), Taxable (brokerage, higher real return, capital-gains tax on sale), and Retirement (401(k)/IRA, higher real return, ordinary income tax + possible early-withdrawal penalty).\n\nThe bucket determines how the projection grows the money each year, how withdrawals are taxed, and what order the engine pulls from for retirement spending or one-off purchases (default: Taxable → HYSA → Retirement).\n\nCairn separately tracks `kind` (UI categorization: home / mortgage / other_asset / other_liability) which doesn\'t participate in bucket math. Only investable accounts have a bucket.',
    links: [],
    related: ['hysa', 'taxable_brokerage', '401k', 'sleeve'],
    category: 'cairn_terms',
  },
  {
    id: 'sleeve',
    term: 'Sleeve (tax sleeve)',
    aliases: ['tax sleeve', 'sleeve'],
    short:
      'A sub-category within a retirement account, distinguishing pretax dollars from Roth dollars.',
    long:
      'A 401(k) account can hold pretax dollars, Roth dollars, and after-tax dollars at the same time — these are called "sleeves" because they sit alongside each other inside the same account, with different tax treatment.\n\nCairn tracks the tax sleeve at the account level (pretax vs. roth) for retirement accounts. This drives whether contributions to that account count against the 402(g) limit (pretax/roth) or only against the 415(c) limit (after-tax), and how withdrawals get taxed.\n\nSleeve is a Cairn convenience term — there\'s no IRS form labeled "sleeve." But savers and 401(k) administrators use the language all the time when distinguishing the post-tax buckets that share an account.',
    links: [
      {
        label: 'IRS — Roth Comparison Chart',
        href: 'https://www.irs.gov/retirement-plans/roth-comparison-chart',
      },
    ],
    related: ['pretax_401k', 'roth_401k', 'after_tax_401k', 'mega_backdoor'],
    category: 'cairn_terms',
  },
  {
    id: 'scenario',
    term: 'Scenario (Cairn)',
    aliases: ['scenario', 'plan'],
    short:
      'A named what-if plan in Cairn — an ordered list of life events that perturb your projection.',
    long:
      'A Scenario is Cairn\'s top-level "what-if" plan. Each one has a name, color, icon, and a list of life events (purchases, home moves, retire-at-X-not-Y, sabbaticals, Roth conversions, windfalls).\n\nOnly one scenario is "active" at a time — that\'s what the dashboard, projection chart, and tax view all reflect. You can also add scenarios to the "compare" set, which the chart overlays in distinct colors so you can see how different choices shape your future side-by-side.\n\nScenarios are the unit of branching — the way to ask "what if I bought a house in 2028?" without permanently committing to the assumption.',
    links: [],
    related: ['event', 'funding_scenario'],
    category: 'cairn_terms',
  },
  {
    id: 'event',
    term: 'Event (Cairn)',
    aliases: ['event', 'life event'],
    short:
      'An action that perturbs the projection — purchase, gift, home buy/sell, refinance, income change, retire, etc.',
    long:
      'An Event is the smallest unit of "something happens" in a scenario. There are 13 event kinds — purchase, gift, home_buy, home_sell, refinance, income_change, contribution_change, job_gap, windfall, roth_conversion, social_security, pension, retire — each with its own fields and timing.\n\nEvents are anchored to a year offset relative to your starting calendar year ("year 0 is 2026"), so a purchase with yearOffset=3 happens in 2029. Each event can be temporarily disabled with a toggle to A/B-test its impact.\n\nSpending events (purchase, gift, home_buy) attach a Funding Scenario describing exactly which buckets pay and how — Cairn computes the tax consequences of those funding choices automatically.',
    links: [],
    related: ['scenario', 'funding_scenario'],
    category: 'cairn_terms',
  },
  {
    id: 'funding_scenario',
    term: 'Funding scenario',
    aliases: ['funding scenario', 'funding'],
    short:
      'How an outflow is paid for: HYSA, taxable sale, mortgage, loan, cash flow. Tax-aware.',
    long:
      'A Funding Scenario describes exactly how a spending event (purchase, gift, home buy/sell) is paid for. It\'s a list of "sources" with amounts: HYSA, taxable bucket sale (with cost basis tracked for capital-gains tax), mortgage, auto/personal/HELOC/401(k) loan, or cash flow (reduces saving instead of selling).\n\nThe engine handles the tax consequences automatically: capital gains tax on taxable sales, ordinary income + 10% penalty on pre-59½ retirement pulls, §121 exclusion on home sales, principal/interest splits on new loans.\n\nFor a $100K kitchen remodel you might fund as: HYSA $30K + Taxable LTCG $40K + HELOC $30K — Cairn would compute exact taxes due, the cash actually pulled from each bucket, and add the HELOC as an amortizing liability going forward.',
    links: [],
    related: ['event', 'scenario', 'ltcg', 'section_121', '401k_loan'],
    category: 'cairn_terms',
  },
  {
    id: 'contribution_rule',
    term: 'Contribution rule (Cairn)',
    aliases: ['contribution rule'],
    short:
      'A recurring deposit into an account: amount or percent of pay, on some schedule, with a tax treatment.',
    long:
      'A Contribution Rule tells the engine "every year, X dollars (or X% of pay) flows into this account, on this schedule, with this tax treatment." Together they form your authored savings intent.\n\nRules have:\n- Mode: fixed_dollar or percent_of_pay (with optional baseOnly to exclude bonus).\n- Schedule: monthly, with-paycheck, lump_sum (specific month), or with-bonus.\n- Tax treatment: pretax / roth_401k / after_tax_401k / mega_backdoor_to_roth / roth_ira / after_tax_brokerage. This is what determines which IRS limit they count against.\n- Target: a bucket or a specific account.\n\nThe engine applies rules in order, automatically capping at IRS limits and surfacing where you got trimmed (so you can re-route the spillover).',
    links: [],
    related: ['irs_402g', 'irs_415c', 'ira_limit'],
    category: 'cairn_terms',
  },
  {
    id: 'time_weighted_growth',
    term: 'Time-weighted growth',
    aliases: ['time weighted growth'],
    short:
      'Cairn\'s growth math: a January lump-sum earns full-year return; a monthly stream earns ~6.5 months of return on average.',
    long:
      'Cairn doesn\'t pretend that all annual contributions land at the start of the year. A January lump-sum gets a full year of compounding; a December lump-sum gets none. Money contributed monthly across the year gets, on average, about 6.5 months of return.\n\nThe engine encodes this as a "time-weight" factor per contribution schedule:\n- lump_sum (January): 1.0 (full year)\n- with_bonus (configurable month): partial year based on month\n- monthly / with-paycheck: 0.54 (≈ half a year + a bit)\n\nThis matters more than it sounds — a $20,000 January contribution at 6% real return earns $1,200, while the same $20,000 spread monthly earns about $650. Cairn surfaces this honestly so your projection reflects the reality of paycheck-driven savings.',
    links: [
      {
        label: 'Investopedia — Time-Weighted Rate of Return',
        href: 'https://www.investopedia.com/terms/t/time-weightedror.asp',
      },
    ],
    related: ['contribution_rule', 'real_return'],
    category: 'cairn_terms',
  },
  {
    id: 'overstretched',
    term: 'Overstretched (Cairn)',
    aliases: ['overstretched'],
    short:
      'A Cairn flag for a year where mandatory expenses exceed after-tax income — Cairn auto-trims contributions to balance.',
    long:
      'Cairn flags a year as "overstretched" when your after-tax income (wages + bonus + extra income, minus federal/state/FICA tax) doesn\'t cover your mandatory spending (housing, healthcare, mortgage P&I, household spend) — leaving zero or negative dollars to save.\n\nWhen this happens, the engine quietly auto-trims your taxable and HYSA contributions in priority order to make the year balance. It doesn\'t crash, it doesn\'t hide it — it shows up in the projection table with a chip and surfaces as a "things to know" item with suggestions ("lower spend, push retirement out a year").\n\nSeeing overstretched years in your projection isn\'t a crisis — it\'s information. The plan still adds up. But it tells you the savings rate you authored is more than the income/expense math actually allows.',
    links: [],
    related: ['contribution_rule'],
    category: 'cairn_terms',
  },
  {
    id: 'percentile',
    term: 'Percentile',
    aliases: ['percentile rank', 'pct rank'],
    short:
      'A "you are richer than N% of the cohort" rank. The 75th percentile means 25% of households have more, 75% have less.',
    long:
      'Percentiles split a distribution into 100 equal-count slices. If your net worth is at the 80th percentile for your age, then out of every 100 households in your age bucket, 79 have less than you and 20 have more.\n\nPercentiles are robust to outliers in a way that means and averages aren\'t. A handful of billionaires doesn\'t change the median (50th percentile) at all, but it can make the "average" net worth misleading. That\'s why Cairn shows percentile ranks rather than peer averages.\n\nThe headline number you see on each Cohorts card is computed by log-linear interpolation between seven anchor percentiles (10, 25, 50, 75, 90, 95, 99) published by the Federal Reserve. Above the 99th or below the 10th, Cairn says so explicitly rather than guessing.',
    links: [
      {
        label: 'Wikipedia — Percentile',
        href: 'https://en.wikipedia.org/wiki/Percentile',
      },
    ],
    related: ['scf'],
    category: 'cairn_terms',
  },
  {
    id: 'scf',
    term: 'Survey of Consumer Finances (SCF)',
    aliases: ['SCF', 'Federal Reserve survey'],
    short:
      'A triennial Federal Reserve household survey that is the gold-standard source for US wealth distribution data.',
    long:
      'The SCF is run every three years by the Federal Reserve in cooperation with the IRS Statistics of Income division. It oversamples wealthy households (which a random sample misses) so it can accurately measure the upper tail of the wealth distribution — exactly where retirement planning gets interesting.\n\nCairn bundles a small (~17 KB) processed slice of the 2022 SCF Summary Extract: anchor percentiles for net worth, financial assets, non-financial assets, retirement accounts, home equity, and liquid assets — broken down by age band and income band. The raw data is downloaded at build time by `npm run data:refresh` and is never accessed at runtime.\n\nDollar values are inflated from 2022 dollars to current-year dollars using a hardcoded CPI ratio. The next SCF release covers survey year 2025 and will be published in 2027.',
    currentValue: 'Latest data: SCF 2022 (released April 2024).',
    links: [
      {
        label: 'Federal Reserve — Survey of Consumer Finances',
        href: 'https://www.federalreserve.gov/econres/scfindex.htm',
      },
      {
        label: 'Fed Bulletin: Changes in U.S. Family Finances 2019–2022',
        href: 'https://www.federalreserve.gov/publications/files/scf23.pdf',
      },
    ],
    related: ['percentile'],
    category: 'cairn_terms',
  },
];

// ────────────────────────────────────────────────────────────────────────────
// Lookup helpers.
// ────────────────────────────────────────────────────────────────────────────

const TERMS_BY_ID = new Map<string, GlossaryTerm>(
  GLOSSARY.map(t => [t.id, t]),
);

/** Look up a term by its stable `id`. Returns `undefined` if unknown. */
export function getTerm(id: string): GlossaryTerm | undefined {
  return TERMS_BY_ID.get(id);
}

/** Find a term by id, exact term match, or alias (case-insensitive). */
export function findByAlias(query: string): GlossaryTerm | undefined {
  if (!query) return undefined;
  const q = query.trim().toLowerCase();
  if (!q) return undefined;
  // Direct id hit.
  const direct = TERMS_BY_ID.get(q);
  if (direct) return direct;
  // Otherwise scan term name + aliases.
  for (const t of GLOSSARY) {
    if (t.term.toLowerCase() === q) return t;
    if (t.aliases?.some(a => a.toLowerCase() === q)) return t;
  }
  return undefined;
}

/** Lightweight relevance-scored search across term + aliases + short text.
 *  Used by the global glossary page's filter input. */
export function searchTerms(query: string): GlossaryTerm[] {
  const q = query.trim().toLowerCase();
  if (!q) return GLOSSARY;
  return GLOSSARY.filter(t => {
    if (t.id.includes(q)) return true;
    if (t.term.toLowerCase().includes(q)) return true;
    if (t.short.toLowerCase().includes(q)) return true;
    if (t.aliases?.some(a => a.toLowerCase().includes(q))) return true;
    return false;
  });
}
